import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { useSession } from '@/shared/auth/SessionProvider';
import { ApiError } from '@/shared/api';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { PasswordInput } from '@/shared/components/ui/password-input';
import { FornitaliaLogo } from '@/shared/components/brand/FornitaliaLogo';
import { SplashScreen } from '@/shared/components/brand/SplashScreen';
import { APP_VERSION } from '@/shared/constants';

const schema = z.object({
  usuarioApp: z.string().min(1, 'Ingresá tu usuario'),
  password: z.string().min(1, 'Ingresá tu contraseña'),
});

type FormValues = z.infer<typeof schema>;

export function LoginPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, loading, signIn } = useSession();
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { usuarioApp: '', password: '' },
  });

  // All hooks MUST be called before any early return — React counts them by
  // position, not by name.
  const reduce = useReducedMotion();

  if (loading && !profile) {
    return <SplashScreen />;
  }

  if (profile) {
    const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
    if (from && from !== '/login') return <Navigate to={from} replace />;
    // Direct perfil check — `hasRole` returns true for visitante on every
    // role query (universal viewer), which would mis-route them to
    // /mantenimiento here. Mantenimiento-specific landing only fires for
    // an actual mantenimiento user. Nova lands directly on its actionable
    // queue to skip the home page hop.
    if (profile.perfil === 'mantenimiento') return <Navigate to="/mantenimiento" replace />;
    if (profile.perfil === 'nova') return <Navigate to="/administracion" replace />;
    return <Navigate to="/" replace />;
  }

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitting(true);
    form.clearErrors('root');
    try {
      const next = await signIn(values);
      if (next.perfil === 'mantenimiento') navigate('/mantenimiento', { replace: true });
      else if (next.perfil === 'nova') navigate('/administracion', { replace: true });
      else navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        if (err.code === 'user_not_found') {
          form.setError('usuarioApp', {
            type: 'server',
            message: 'El usuario no existe.',
          });
          form.setFocus('usuarioApp');
        } else if (err.code === 'invalid_password') {
          form.setError('password', {
            type: 'server',
            message: 'La contraseña es incorrecta.',
          });
          form.resetField('password', { keepError: true });
          form.setFocus('password');
        } else {
          form.setError('root', {
            type: 'server',
            message: 'No se pudo iniciar sesión. Verificá tus credenciales.',
          });
        }
      } else {
        const message = err instanceof Error ? err.message : 'No se pudo iniciar sesión';
        toast.error('Error de autenticación', { description: message });
      }
    } finally {
      setSubmitting(false);
    }
  });

  const disabled = submitting || loading;
  const rootError = form.formState.errors.root?.message;

  return (
    <div className="flex min-h-dvh w-full items-center justify-center bg-background p-4">
      <motion.div
        className="w-full max-w-md rounded-2xl bg-card p-8 shadow-card"
        initial={reduce ? false : { opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="flex flex-col items-center gap-2 text-center">
          <FornitaliaLogo variant="horizontal" size="md" />
          <h1 className="mt-6 text-4xl font-semibold tracking-tight text-foreground">
            Bienvenido
          </h1>
          <p className="text-sm text-muted-foreground">Inicia sesión para continuar</p>
        </div>

        <form onSubmit={onSubmit} noValidate className="mt-8 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="usuarioApp">Nombre de usuario</Label>
            <Input
              id="usuarioApp"
              variant="filled"
              type="text"
              placeholder="Usuario"
              autoComplete="username"
              autoFocus
              aria-invalid={Boolean(form.formState.errors.usuarioApp)}
              {...form.register('usuarioApp')}
            />
            {form.formState.errors.usuarioApp && (
              <p role="alert" className="text-xs font-medium text-destructive">
                {form.formState.errors.usuarioApp.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Contraseña</Label>
            <PasswordInput
              id="password"
              variant="filled"
              placeholder="Password"
              autoComplete="current-password"
              aria-invalid={Boolean(form.formState.errors.password)}
              {...form.register('password')}
            />
            {form.formState.errors.password && (
              <p role="alert" className="text-xs font-medium text-destructive">
                {form.formState.errors.password.message}
              </p>
            )}
          </div>

          {rootError && (
            <div
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive"
            >
              {rootError}
            </div>
          )}

          <Button type="submit" size="lg" className="mt-2 w-full" disabled={disabled}>
            {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            {submitting ? 'Ingresando…' : 'Iniciar sesión'}
          </Button>

          <p className="pt-1 text-center text-[11px] text-muted-foreground">{APP_VERSION}</p>
        </form>
      </motion.div>
    </div>
  );
}
